import { useCallback, useEffect, useState } from 'react'

import { ApiError } from '../../lib/api'
import { biz, type Contact } from '../../lib/business'
import { useToast } from '../../app/Toasts'

const EMPTY: Partial<Contact> & { name: string } = {
  name: '', is_customer: true, is_vendor: false, gstin: '', email: '',
  phone: '', billing_address: '', city: '', state: '', pincode: '',
  payment_terms_days: 30,
}

export default function ContactsPage() {
  const { push } = useToast()
  const [rows, setRows] = useState<Contact[]>([])
  const [q, setQ] = useState('')
  const [role, setRole] = useState<'' | 'customer' | 'vendor'>('')
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<(Partial<Contact> & { name: string }) | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try {
      const { items } = await biz.contacts({ q: q || undefined, role: role || undefined })
      setRows(items)
    } catch { /* listing failures are not worth a toast */ } finally {
      setLoading(false)
    }
  }, [q, role])

  useEffect(() => {
    const t = window.setTimeout(load, q ? 250 : 0)
    return () => window.clearTimeout(t)
  }, [load, q])

  const save = async () => {
    if (!editing) return
    setBusy(true)
    try {
      if (editing.id) await biz.updateContact(editing.id, editing as Contact)
      else await biz.createContact(editing)
      push('success', editing.id ? 'Contact updated' : 'Contact created')
      setEditing(null)
      load()
    } catch (e) {
      push('error', 'Could not save',
        e instanceof ApiError ? e.message : undefined)
    } finally { setBusy(false) }
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="card">
        <div className="card-head">
          <span className="card-title">Contacts</span>
          <div className="grow" />
          <input
            className="input" style={{ width: 220 }} placeholder="Search name, GSTIN, email"
            value={q} onChange={(e) => setQ(e.target.value)}
          />
          {(['', 'customer', 'vendor'] as const).map((r) => (
            <button key={r || 'all'}
              className={`btn btn-sm${role === r ? ' btn-primary' : ''}`}
              onClick={() => setRole(r)}>
              {r === '' ? 'All' : r === 'customer' ? 'Customers' : 'Vendors'}
            </button>
          ))}
          <button className="btn btn-sm btn-primary" onClick={() => setEditing({ ...EMPTY })}>
            New contact
          </button>
        </div>

        {loading ? (
          <div className="card-body"><div className="skeleton" style={{ height: 120 }} /></div>
        ) : rows.length === 0 ? (
          <div className="empty">
            <div className="empty-title">No contacts yet</div>
            <div>Customers and vendors live here. Vendors are also created
              automatically when you approve an uploaded invoice.</div>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Name</th><th>Role</th><th className="col-optional">GSTIN</th><th>State</th>
                  <th className="col-optional">Contact</th><th className="num col-optional">Terms</th><th />
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 520 }}>{c.name}</td>
                    <td>
                      <div className="row" style={{ gap: 4 }}>
                        {c.is_customer && <span className="pill pill-accent">customer</span>}
                        {c.is_vendor && <span className="pill pill-mute">vendor</span>}
                      </div>
                    </td>
                    <td className="mono col-optional" style={{ fontSize: 12 }}>
                      {c.gstin ?? <span className="muted">—</span>}
                    </td>
                    <td>{c.state_name ?? <span className="muted">—</span>}</td>
                    <td className="muted col-optional" style={{ fontSize: 12 }}>
                      {c.email || c.phone || '—'}
                    </td>
                    <td className="num col-optional">{c.payment_terms_days}d</td>
                    <td className="num">
                      <button className="btn btn-sm" onClick={() => setEditing({ ...c })}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <div className="card">
          <div className="card-head">
            <span className="card-title">
              {editing.id ? `Edit ${editing.name}` : 'New contact'}
            </span>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Name" required>
                <input className="input" value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </Field>
              <Field label="GSTIN" hint="15 characters; checked on save">
                <input className="input input-mono" value={editing.gstin ?? ''}
                  maxLength={15}
                  onChange={(e) => setEditing({ ...editing, gstin: e.target.value.toUpperCase() })} />
              </Field>
              <Field label="Email">
                <input className="input" value={editing.email ?? ''}
                  onChange={(e) => setEditing({ ...editing, email: e.target.value })} />
              </Field>
              <Field label="Phone">
                <input className="input" value={editing.phone ?? ''}
                  onChange={(e) => setEditing({ ...editing, phone: e.target.value })} />
              </Field>
              <Field label="City">
                <input className="input" value={editing.city ?? ''}
                  onChange={(e) => setEditing({ ...editing, city: e.target.value })} />
              </Field>
              <Field label="Payment terms (days)">
                <input className="input" type="number" value={editing.payment_terms_days ?? 30}
                  onChange={(e) => setEditing({ ...editing, payment_terms_days: Number(e.target.value) })} />
              </Field>
              <Field label="Billing address">
                <textarea className="textarea" rows={2} value={editing.billing_address ?? ''}
                  onChange={(e) => setEditing({ ...editing, billing_address: e.target.value })} />
              </Field>
              <Field label="Roles">
                <div className="row" style={{ paddingTop: 6 }}>
                  <label className="row" style={{ gap: 5 }}>
                    <input type="checkbox" checked={!!editing.is_customer}
                      onChange={(e) => setEditing({ ...editing, is_customer: e.target.checked })} />
                    Customer
                  </label>
                  <label className="row" style={{ gap: 5 }}>
                    <input type="checkbox" checked={!!editing.is_vendor}
                      onChange={(e) => setEditing({ ...editing, is_vendor: e.target.checked })} />
                    Vendor
                  </label>
                </div>
              </Field>
            </div>
            <div className="row" style={{ marginTop: 14 }}>
              <button className="btn btn-primary" onClick={save} disabled={busy || !editing.name}>
                {busy && <span className="spinner" />}Save
              </button>
              <button className="btn" onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export function Field({ label, children, hint, required }: {
  label: string; children: React.ReactNode; hint?: string; required?: boolean
}) {
  return (
    <div className="form-row" style={{ marginBottom: 0 }}>
      <label>{label}{required && <span style={{ color: 'var(--crit)' }}> *</span>}</label>
      {children}
      {hint && <span className="muted" style={{ fontSize: 11.5 }}>{hint}</span>}
    </div>
  )
}
